import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import Footer from "@/components/Footer";
import { ArrowLeft } from "lucide-react";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { sanitizeHtml } from "@/lib/sanitize";

export default function TermsPage() {
  const { lang } = useI18n();
  const content = lang === "it" ? IT : EN;
  useDocumentMeta({
    title: lang === "it"
      ? "Termini e Condizioni · Gallura Drive"
      : "Terms and Conditions · Gallura Drive",
    description: lang === "it"
      ? "Termini e condizioni del servizio di autista privato (NCC) Gallura Drive: prenotazione, tariffe, cancellazione, responsabilità."
      : "Terms and conditions of the Gallura Drive private chauffeur (NCC) service: booking, rates, cancellation, liability.",
    canonical: "https://galluradrive.it/terms",
  });
  return (
    <div data-testid="terms-page" className="min-h-screen bg-[#F9F7F3]">
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="terms-back" className="flex items-center gap-2 text-slate-600 hover:brand-text">
            <ArrowLeft className="w-4 h-4" /> Gallura Drive
          </Link>
          <Link to="/privacy" className="text-sm text-slate-600 hover:brand-text">Privacy</Link>
        </div>
      </header>
      <article className="max-w-3xl mx-auto px-6 py-12 prose prose-slate">
        <h1 className="font-display text-4xl brand-text mb-2">{content.title}</h1>
        <p className="text-sm text-slate-500 mb-8">{content.updated}</p>
        <div className="space-y-6 text-slate-700 leading-relaxed" dangerouslySetInnerHTML={sanitizeHtml(content.body)} />
      </article>
      <Footer />
    </div>
  );
}

const IT = {
  title: "Termini e Condizioni del Servizio",
  updated: "Ultimo aggiornamento: febbraio 2026",
  body: `
    <h2 class="font-display text-2xl brand-text mt-8">1. Titolare del servizio</h2>
    <p>Il servizio di autista privato è erogato da <strong>Gallura Drive</strong> con sede in Olbia (SS), P.IVA in corso di registrazione, telefono +39 347 001 1246, email info@galluradrive.it ("Titolare").</p>

    <h2 class="font-display text-2xl brand-text mt-8">2. Oggetto del servizio</h2>
    <p>Il Titolare offre servizi di transfer e tour privati con conducente nel territorio della Gallura (Sardegna), a bordo di vettura Chrysler 300C Touring fino a 4 passeggeri.</p>

    <h2 class="font-display text-2xl brand-text mt-8">3. Prenotazione</h2>
    <p>La prenotazione si effettua tramite il form sul sito o contattando direttamente il Titolare. La richiesta si considera <strong>confermata</strong> solo dopo conferma esplicita da parte del Titolare via telefono, WhatsApp o email.</p>

    <h2 class="font-display text-2xl brand-text mt-8">4. Tariffe e pagamento</h2>
    <p>Le tariffe sono indicate sul sito e si intendono comprensive di IVA. Il pagamento avviene <strong>direttamente in auto</strong> al momento del servizio, in contanti o tramite POS (carte di debito/credito accettate). Non è previsto pagamento online anticipato.</p>

    <h2 class="font-display text-2xl brand-text mt-8">5. Cancellazione</h2>
    <p>Il Cliente può cancellare gratuitamente la prenotazione <strong>fino a 24 ore prima</strong> dell'orario di pickup. Cancellazioni effettuate entro le 24 ore o no-show possono comportare l'addebito del 50% della tariffa pattuita.</p>

    <h2 class="font-display text-2xl brand-text mt-8">6. Ritardi e impedimenti</h2>
    <p>Il Titolare non è responsabile per ritardi causati da eventi di forza maggiore (traffico, condizioni meteo, scioperi, ritardi voli/traghetti). In caso di ritardo del volo del Cliente, il Titolare attenderà gratuitamente fino a 60 minuti dopo l'orario di atterraggio reale.</p>

    <h2 class="font-display text-2xl brand-text mt-8">7. Obblighi del Cliente</h2>
    <p>Il Cliente si impegna a fornire dati di contatto corretti, presentarsi puntualmente al pickup, comportarsi in modo civile a bordo. È vietato fumare, consumare cibo o bevande senza consenso, danneggiare la vettura. Eventuali danni saranno addebitati al Cliente.</p>

    <h2 class="font-display text-2xl brand-text mt-8">8. Bagagli</h2>
    <p>È compreso il trasporto di bagagli standard (1 valigia + 1 bagaglio a mano per passeggero). Bagagli extra o ingombranti vanno comunicati in fase di prenotazione.</p>

    <h2 class="font-display text-2xl brand-text mt-8">9. Limitazione di responsabilità</h2>
    <p>La responsabilità del Titolare è limitata a quanto previsto dalla normativa italiana sui trasporti privati con conducente (L. 21/1992 e successive modifiche). Il Titolare è regolarmente assicurato per la responsabilità civile.</p>

    <h2 class="font-display text-2xl brand-text mt-8">10. Foro competente</h2>
    <p>Per ogni controversia è competente in via esclusiva il Foro di Tempio Pausania (SS).</p>

    <h2 class="font-display text-2xl brand-text mt-8">11. Modifiche</h2>
    <p>Il Titolare si riserva il diritto di modificare i presenti Termini in qualsiasi momento. Le modifiche saranno pubblicate su questa pagina con data di aggiornamento.</p>
  `,
};

const EN = {
  title: "Terms and Conditions of Service",
  updated: "Last updated: February 2026",
  body: `
    <h2 class="font-display text-2xl brand-text mt-8">1. Service provider</h2>
    <p>The private chauffeur service is provided by <strong>Gallura Drive</strong>, based in Olbia (SS), Italy. VAT registration in progress. Phone +39 347 001 1246, email info@galluradrive.it ("Provider").</p>

    <h2 class="font-display text-2xl brand-text mt-8">2. Scope</h2>
    <p>The Provider offers private transfer and tour services with chauffeur within the Gallura region (Sardinia), aboard a Chrysler 300C Touring, up to 4 passengers.</p>

    <h2 class="font-display text-2xl brand-text mt-8">3. Booking</h2>
    <p>Bookings are made via the website form or by contacting the Provider directly. A booking is considered <strong>confirmed</strong> only upon explicit confirmation by the Provider via phone, WhatsApp or email.</p>

    <h2 class="font-display text-2xl brand-text mt-8">4. Rates and payment</h2>
    <p>Rates are listed on the website and are VAT-inclusive. Payment is made <strong>directly in the car</strong> at service time, in cash or by POS (debit/credit cards accepted). No advance online payment is required.</p>

    <h2 class="font-display text-2xl brand-text mt-8">5. Cancellation</h2>
    <p>Free cancellation is available <strong>up to 24 hours</strong> before pickup. Cancellations within 24 hours or no-shows may result in a 50% charge of the agreed fare.</p>

    <h2 class="font-display text-2xl brand-text mt-8">6. Delays and force majeure</h2>
    <p>The Provider is not liable for delays caused by force majeure (traffic, weather, strikes, flight/ferry delays). In case of flight delay, the Provider will wait at no extra cost up to 60 minutes after the actual landing time.</p>

    <h2 class="font-display text-2xl brand-text mt-8">7. Customer obligations</h2>
    <p>The Customer agrees to provide correct contact details, arrive punctually, behave properly on board. Smoking, eating, drinking without consent, or damaging the vehicle is prohibited. Any damage will be charged to the Customer.</p>

    <h2 class="font-display text-2xl brand-text mt-8">8. Luggage</h2>
    <p>Standard luggage is included (1 suitcase + 1 carry-on per passenger). Extra or oversized luggage must be declared at booking time.</p>

    <h2 class="font-display text-2xl brand-text mt-8">9. Liability</h2>
    <p>The Provider's liability is limited to what is set out by Italian law on private chauffeur transport (L. 21/1992 and subsequent amendments). The Provider holds valid civil liability insurance.</p>

    <h2 class="font-display text-2xl brand-text mt-8">10. Governing law</h2>
    <p>Any dispute shall be exclusively submitted to the Court of Tempio Pausania (SS), Italy.</p>

    <h2 class="font-display text-2xl brand-text mt-8">11. Changes</h2>
    <p>The Provider reserves the right to amend these Terms at any time. Changes will be posted here with the updated date.</p>
  `,
};
